import { PERMISSIONS } from "@/constants/permissions";
import { Team, TeamMember, TeamRole } from "@/types/team.types";

/**
 * Builds a map from user ID to display label (name ?? email ?? id) from all team members.
 * Useful for dropdowns and preset entity labels.
 */
export function getUserIdToLabelMap(teams: Team[]): Map<string, string> {
    const map = new Map<string, string>();
    for (const team of teams) {
        for (const member of team.members) {
            if (member.user?.id) {
                const label = member.user.name ?? member.user.email ?? member.user.id;
                map.set(member.user.id, label);
            }
        }
    }
    return map;
}

/**
 * Gets unique team members by ID and optionally filters out a specific user
 * This is a pure utility function that can be reused across components
 */
export const getUniqueMembers = (members: TeamMember[], excludeUserId?: string): TeamMember[] => {
    // Remove duplicates by ID
    const uniqueMembers = members.filter((member, index, self) => index === self.findIndex((m) => m.id === member.id));

    // Filter out excluded user if provided
    return excludeUserId ? uniqueMembers.filter((member) => member.id !== excludeUserId) : uniqueMembers;
};

export const sortTeamsBySortOrder = (teams: Team[]): Team[] => {
    return [...teams].sort((a, b) => {
        const sortOrderDiff = (b.sortOrder ?? 0) - (a.sortOrder ?? 0);
        if (sortOrderDiff !== 0) return sortOrderDiff;
        return a.name.localeCompare(b.name);
    });
};

export const getHighestPriorityTeam = (teams: Team[]): Team | undefined => {
    return sortTeamsBySortOrder(teams)[0];
};

export function isSameTeamMember(member: TeamMember, userId: string | undefined): boolean {
    if (!userId) {
        return false;
    }

    return member.id === userId || member.user?.id === userId;
}

export function canAccessTeamMemberSelection(userPermissionCodes: string[]): boolean {
    return (
        userPermissionCodes.includes(PERMISSIONS.CALL_SVC_ADMIN_BASE) ||
        userPermissionCodes.includes(PERMISSIONS.CALL_SVC_TEAM_LEAD_BASE) ||
        userPermissionCodes.includes(PERMISSIONS.CALL_CALLS_SELECT_MEMBER) ||
        userPermissionCodes.includes(PERMISSIONS.CALL_APPOINTMENTS_SELECT_MEMBER) ||
        userPermissionCodes.includes(PERMISSIONS.CALL_KPIS_SELECT_MEMBER)
    );
}

export const getAccessibleTeams = (allTeams: Team[], userPermissionCodes: string[], userId?: string): Team[] => {
    const hasAdminPermission = userPermissionCodes.includes(PERMISSIONS.CALL_SVC_ADMIN_BASE);

    if (hasAdminPermission) return allTeams;

    const canPinAgents = userPermissionCodes.includes(PERMISSIONS.CALL_SENIOR_SETTER);

    if ((!canAccessTeamMemberSelection(userPermissionCodes) && !canPinAgents) || !userId) {
        return [];
    }

    const ledTeams = allTeams.filter((team) =>
        team.members.some((member) => isSameTeamMember(member, userId) && member.role === TeamRole.TEAM_LEAD)
    );

    if (ledTeams.length > 0) {
        return ledTeams;
    }

    // Senior setters and select-member users without a team-lead seat see the teams they belong to.
    return allTeams.filter((team) => team.members.some((member) => isSameTeamMember(member, userId)));
};

export const getAccessibleTeamMembers = (
    teams: Team[],
    userPermissionCodes: string[],
    currentUserId?: string
): TeamMember[] => {
    if (!canAccessTeamMemberSelection(userPermissionCodes)) return [];

    const allMembers = teams.flatMap((team) => team.members);

    return getUniqueMembers(allMembers, currentUserId);
};
